import { t, useI18n } from '../../i18n';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { dutyBoardService, type SiteRow } from '../../services/dutyBoardService';
import {
  shiftService,
  type Shift,
  type ShiftWelfareMode,
} from '../../services/shiftService';
import {
  formatAbsoluteMinute,
  formatDateKeyLabel,
  formatDayNumber,
  formatDayOfWeek,
  formatWeekRange,
  getCurrentDateKeyInTimeZone,
  getSiteCoverageWindow,
  getWeekDates,
  resolveSiteTimeZone,
  shiftDateKey,
} from '../../utils/siteScheduling';
import { getAvatarAccentStyle } from '../../utils/avatarAccent';
import ShiftSegmentFlow from './ShiftSegmentFlow';
import './GuardShiftsPage.css';

type ViewMode = 'day' | 'week';

interface GuardShiftGroup {
  guardId: string;
  guardName: string;
  guardAvatarUrl: string | null;
  shifts: Shift[];
  totalMinutes: number;
}

interface GuardWeekGroup {
  guardId: string;
  guardName: string;
  guardAvatarUrl: string | null;
  shiftsByDate: Record<string, Shift[]>;
  totalMinutes: number;
  totalShifts: number;
  firstStartMinute: number;
}

const normaliseSearch = (value: string): string => value.trim().toLowerCase();

const initials = (name: string): string =>
  name
    .split(' ')
    .slice(0, 2)
    .map((word) => word[0] ?? '')
    .join('')
    .toUpperCase();

const formatDuration = (minutes: number): string => {
  const safeMinutes = Math.max(0, Math.round(minutes));
  const hours = Math.floor(safeMinutes / 60);
  const remainingMinutes = safeMinutes % 60;

  if (hours === 0) return `${remainingMinutes}m`;
  if (remainingMinutes === 0) return `${hours}h`;
  return `${hours}h ${remainingMinutes}m`;
};

function compareShifts(left: Shift, right: Shift): number {
  return (
    left.slot_date.localeCompare(right.slot_date)
    || (left.start_minute - right.start_minute)
    || left.guard_name.localeCompare(right.guard_name)
  );
}

function firstDutyLabel(shift: Shift): string {
  const firstDuty = shift.segments.find((segment) => segment.segment_type === 'duty');
  if (firstDuty) return firstDuty.label;
  return shift.segments[0]?.label ?? 'Shift';
}

function matchesGuardQuery(guardName: string, query: string): boolean {
  if (!query) return true;
  return normaliseSearch(guardName).includes(query);
}

export default function GuardShiftsPage() {
  useI18n();
  const [sites, setSites] = useState<SiteRow[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [selectedDate, setSelectedDate] = useState(() => getCurrentDateKeyInTimeZone(resolveSiteTimeZone(undefined)));
  const [viewMode, setViewMode] = useState<ViewMode>('day');
  const [guardQuery, setGuardQuery] = useState('');
  const [dayShifts, setDayShifts] = useState<Shift[]>([]);
  const [weekShiftsByDate, setWeekShiftsByDate] = useState<Record<string, Shift[]>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [savingWelfareShiftId, setSavingWelfareShiftId] = useState<string | null>(null);

  const hasInitialisedSiteDateRef = useRef(false);

  const selectedSite = useMemo(
    () => sites.find((site) => site.id === selectedSiteId) ?? null,
    [selectedSiteId, sites],
  );

  const selectedSiteTimeZone = useMemo(
    () => resolveSiteTimeZone(selectedSite),
    [selectedSite],
  );

  const coverageWindow = useMemo(
    () => getSiteCoverageWindow(selectedSite),
    [selectedSite],
  );

  const weekDates = useMemo(() => getWeekDates(selectedDate), [selectedDate]);
  const weekRangeLabel = useMemo(() => formatWeekRange(weekDates), [weekDates]);
  const todayKey = useMemo(
    () => getCurrentDateKeyInTimeZone(selectedSiteTimeZone),
    [selectedSiteTimeZone],
  );

  const normalisedGuardQuery = useMemo(
    () => normaliseSearch(guardQuery),
    [guardQuery],
  );

  const loadSites = useCallback(async () => {
    try {
      const siteRows = await dutyBoardService.fetchSites();
      setSites(siteRows);
      if (!selectedSiteId && siteRows.length > 0) {
        setSelectedSiteId(siteRows[0].id);
      }
    } catch (err) {
      setError((err as Error).message);
    }
  }, [selectedSiteId]);

  const loadDayShifts = useCallback(async () => {
    if (!selectedSiteId) return;

    setIsLoading(true);
    setError('');

    try {
      const rows = await shiftService.fetchSiteShifts(selectedSiteId, selectedDate);
      setDayShifts(rows);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, [selectedDate, selectedSiteId]);

  const loadWeekShifts = useCallback(async () => {
    if (!selectedSiteId) return;

    setIsLoading(true);
    setError('');

    try {
      const grouped = await shiftService.fetchSiteShiftsRange(
        selectedSiteId,
        weekDates[0],
        weekDates[weekDates.length - 1],
      );
      setWeekShiftsByDate(grouped);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, [selectedSiteId, weekDates]);

  useEffect(() => {
    void loadSites();
  }, [loadSites]);

  useEffect(() => {
    if (!selectedSiteId) return;

    if (viewMode === 'week') {
      void loadWeekShifts();
      return;
    }

    void loadDayShifts();
  }, [loadDayShifts, loadWeekShifts, selectedSiteId, viewMode]);

  useEffect(() => {
    if (!selectedSiteId || hasInitialisedSiteDateRef.current) return;
    setSelectedDate(getCurrentDateKeyInTimeZone(selectedSiteTimeZone));
    hasInitialisedSiteDateRef.current = true;
  }, [selectedSiteId, selectedSiteTimeZone]);

  const handleWelfareModeChange = useCallback(async (
    shiftId: string,
    mode: ShiftWelfareMode,
  ) => {
    setSavingWelfareShiftId(shiftId);
    setError('');
    try {
      await shiftService.setShiftWelfareMode(shiftId, mode);
      setDayShifts((current) => current.map((shift) => (
        shift.id === shiftId ? { ...shift, welfare_check_mode: mode } : shift
      )));
      setWeekShiftsByDate((current) => Object.fromEntries(
        Object.entries(current).map(([dateKey, shifts]) => [
          dateKey,
          shifts.map((shift) => (
            shift.id === shiftId ? { ...shift, welfare_check_mode: mode } : shift
          )),
        ]),
      ));
    } catch (err) {
      setError((err as Error).message || t('Failed to update shift welfare policy'));
    } finally {
      setSavingWelfareShiftId(null);
    }
  }, []);

  const formatMinute = useCallback((minute: number): string => (
    formatAbsoluteMinute(minute, {
      showMidnightAs24: coverageWindow.startMinute === 0
        && minute === coverageWindow.endMinute
        && coverageWindow.endMinute === 1440,
    })
  ), [coverageWindow]);

  const guardGroups = useMemo<GuardShiftGroup[]>(() => {
    const grouped = new Map<string, GuardShiftGroup>();

    [...dayShifts]
      .sort(compareShifts)
      .forEach((shift) => {
        const existing = grouped.get(shift.guard_id);
        const totalMinutes = shift.end_minute - shift.start_minute;

        if (existing) {
          existing.shifts.push(shift);
          existing.totalMinutes += totalMinutes;
          return;
        }

        grouped.set(shift.guard_id, {
          guardId: shift.guard_id,
          guardName: shift.guard_name,
          guardAvatarUrl: shift.guard_avatar_url,
          shifts: [shift],
          totalMinutes,
        });
      });

    return Array.from(grouped.values()).sort((left, right) => {
      const leftStart = left.shifts[0]?.start_minute ?? 0;
      const rightStart = right.shifts[0]?.start_minute ?? 0;
      if (leftStart !== rightStart) return leftStart - rightStart;
      return left.guardName.localeCompare(right.guardName);
    });
  }, [dayShifts]);

  const visibleGuardGroups = useMemo(
    () => guardGroups.filter((group) => matchesGuardQuery(group.guardName, normalisedGuardQuery)),
    [guardGroups, normalisedGuardQuery],
  );

  const weekShiftList = useMemo(
    () => weekDates.flatMap((dateKey) => weekShiftsByDate[dateKey] ?? []),
    [weekDates, weekShiftsByDate],
  );

  const weekGuardGroups = useMemo<GuardWeekGroup[]>(() => {
    const grouped = new Map<string, GuardWeekGroup>();

    [...weekShiftList]
      .sort(compareShifts)
      .forEach((shift) => {
        const existing = grouped.get(shift.guard_id);
        const shiftMinutes = shift.end_minute - shift.start_minute;

        if (existing) {
          if (!existing.shiftsByDate[shift.slot_date]) {
            existing.shiftsByDate[shift.slot_date] = [];
          }
          existing.shiftsByDate[shift.slot_date].push(shift);
          existing.totalMinutes += shiftMinutes;
          existing.totalShifts += 1;
          existing.firstStartMinute = Math.min(existing.firstStartMinute, shift.start_minute);
          return;
        }

        grouped.set(shift.guard_id, {
          guardId: shift.guard_id,
          guardName: shift.guard_name,
          guardAvatarUrl: shift.guard_avatar_url,
          shiftsByDate: { [shift.slot_date]: [shift] },
          totalMinutes: shiftMinutes,
          totalShifts: 1,
          firstStartMinute: shift.start_minute,
        });
      });

    return Array.from(grouped.values()).sort((left, right) => {
      if (left.firstStartMinute !== right.firstStartMinute) {
        return left.firstStartMinute - right.firstStartMinute;
      }
      return left.guardName.localeCompare(right.guardName);
    });
  }, [weekShiftList]);

  const visibleWeekGuardGroups = useMemo(
    () => weekGuardGroups.filter((group) => matchesGuardQuery(group.guardName, normalisedGuardQuery)),
    [normalisedGuardQuery, weekGuardGroups],
  );

  const activeShiftList = useMemo(
    () => (viewMode === 'week'
      ? visibleWeekGuardGroups.flatMap((group) => weekDates.flatMap((dateKey) => group.shiftsByDate[dateKey] ?? []))
      : visibleGuardGroups.flatMap((group) => group.shifts)),
    [viewMode, visibleWeekGuardGroups, visibleGuardGroups, weekDates],
  );

  const summary = useMemo(() => {
    const totalMinutes = activeShiftList.reduce((sum, shift) => sum + (shift.end_minute - shift.start_minute), 0);
    const publishedCount = activeShiftList.filter((shift) => shift.is_published).length;
    const draftCount = activeShiftList.length - publishedCount;
    const totalSegments = activeShiftList.reduce((sum, shift) => sum + shift.source_segment_count, 0);

    return {
      shiftCount: activeShiftList.length,
      guardCount: viewMode === 'week' ? visibleWeekGuardGroups.length : visibleGuardGroups.length,
      publishedCount,
      draftCount,
      totalSegments,
      totalMinutes,
    };
  }, [activeShiftList, viewMode, visibleGuardGroups.length, visibleWeekGuardGroups.length]);

  const hasAnyData = viewMode === 'week' ? weekShiftList.length > 0 : dayShifts.length > 0;
  const hasVisibleData = viewMode === 'week' ? visibleWeekGuardGroups.length > 0 : visibleGuardGroups.length > 0;

  const selectedSiteName = selectedSite?.name ?? 'No site selected';
  const dateNavStep = viewMode === 'week' ? 7 : 1;
  const dateLabel = viewMode === 'week' ? weekRangeLabel : formatDateKeyLabel(selectedDate);

  return (
    <div className="guard-shifts-page">
      <div className="guard-shifts-page__header">
        <div className="guard-shifts-page__intro">
          <span className="guard-shifts-page__eyebrow">{t('Schedule shifts')}</span>
          <div className="guard-shifts-page__title-row">
            <h1 className="guard-shifts-page__title">{t('Merged guard shifts')}</h1>
            <span className="guard-shifts-page__site-pill">{selectedSiteName}</span>
          </div>
          <p className="guard-shifts-page__subtitle">
            {t('Search for a guard, switch between day and week, and inspect the merged duty and break flow that each guard will see once the roster is published.')}
          </p>
        </div>

        <div className="guard-shifts-page__controls">
          <div className="guard-shifts-page__control-group guard-shifts-page__control-group--toggle">
            <button
              type="button"
              className={`guard-shifts-page__toggle-btn${viewMode === 'day' ? ' guard-shifts-page__toggle-btn--active' : ''}`}
              onClick={() => setViewMode('day')}
            >
              {t('Day')}
            </button>
            <button
              type="button"
              className={`guard-shifts-page__toggle-btn${viewMode === 'week' ? ' guard-shifts-page__toggle-btn--active' : ''}`}
              onClick={() => setViewMode('week')}
            >
              {t('Week')}
            </button>
          </div>

          <div className="guard-shifts-page__control-group">
            <label className="guard-shifts-page__field">
              <span>{t('Site')}</span>
              <select
                className="guard-shifts-page__select"
                value={selectedSiteId}
                onChange={(event) => setSelectedSiteId(event.target.value)}
              >
                {sites.map((site) => (
                  <option key={site.id} value={site.id}>{site.name}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="guard-shifts-page__control-group">
            <label className="guard-shifts-page__field">
              <span>{t('Guard Search')}</span>
              <div className="guard-shifts-page__search-wrap">
                <input
                  type="search"
                  className="guard-shifts-page__search"
                  value={guardQuery}
                  onChange={(event) => setGuardQuery(event.target.value)}
                  placeholder={t('Search by guard name')}
                />
                {guardQuery && (
                  <button
                    type="button"
                    className="guard-shifts-page__search-clear"
                    onClick={() => setGuardQuery('')}
                    aria-label={t('Clear search')}
                  >
                    x
                  </button>
                )}
              </div>
            </label>
          </div>

          <div className="guard-shifts-page__control-group">
            <button
              type="button"
              className="guard-shifts-page__nav-btn"
              onClick={() => setSelectedDate((current) => shiftDateKey(current, -dateNavStep))}
              aria-label={viewMode === 'week' ? 'Previous week' : 'Previous day'}
            >
              {'<'}
            </button>
            <div className="guard-shifts-page__date-stack">
              <span className="guard-shifts-page__date-label">{viewMode === 'week' ? 'Week' : 'Date'}</span>
              <span className="guard-shifts-page__date-value">{dateLabel}</span>
            </div>
            <button
              type="button"
              className="guard-shifts-page__nav-btn"
              onClick={() => setSelectedDate((current) => shiftDateKey(current, dateNavStep))}
              aria-label={viewMode === 'week' ? 'Next week' : 'Next day'}
            >
              {'>'}
            </button>
            <button
              type="button"
              className="guard-shifts-page__today-btn"
              onClick={() => setSelectedDate(todayKey)}
            >
              {t('Today')}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="guard-shifts-page__error">
          <span>{error}</span>
          <button type="button" onClick={() => setError('')}>x</button>
        </div>
      )}

      <div className="guard-shifts-page__stats">
        <div className="guard-shifts-page__stat">
          <span>{viewMode === 'week' ? 'Week shifts' : 'Shifts'}</span>
          <strong>{summary.shiftCount}</strong>
        </div>
        <div className="guard-shifts-page__stat">
          <span>{t('Guards')}</span>
          <strong>{summary.guardCount}</strong>
        </div>
        <div className="guard-shifts-page__stat">
          <span>{t('Published')}</span>
          <strong>{summary.publishedCount}</strong>
        </div>
        <div className="guard-shifts-page__stat guard-shifts-page__stat--warning">
          <span>{t('Draft')}</span>
          <strong>{summary.draftCount}</strong>
        </div>
        <div className="guard-shifts-page__stat">
          <span>{t('Segments')}</span>
          <strong>{summary.totalSegments}</strong>
        </div>
        <div className="guard-shifts-page__stat">
          <span>{t('Hours')}</span>
          <strong>{formatDuration(summary.totalMinutes)}</strong>
        </div>
      </div>

      <div className="guard-shifts-page__board">
        {isLoading ? (
          <div className="guard-shifts-page__state">{t('Loading shifts...')}</div>
        ) : !selectedSiteId ? (
          <div className="guard-shifts-page__state">{t('Choose a site to inspect shifts.')}</div>
        ) : !hasAnyData ? (
          <div className="guard-shifts-page__empty">
            <h3>{viewMode === 'week' ? 'No guard shifts for this week' : 'No guard shifts for this day'}</h3>
            <p>
              {viewMode === 'week'
                ? 'Shifts appear here once duties or breaks are assigned to guards on the selected site during this week.'
                : 'Shifts appear here once duties or breaks are assigned to guards on the selected site and date.'}
            </p>
          </div>
        ) : !hasVisibleData ? (
          <div className="guard-shifts-page__empty">
            <h3>{t('No guards match that search')}</h3>
            <p>{t('Try a different name or clear the search to see the full roster again.')}</p>
          </div>
        ) : viewMode === 'week' ? (
          visibleWeekGuardGroups.map((group) => (
            <section key={group.guardId} className="guard-shifts-page__guard-group">
              <div className="guard-shifts-page__guard-header">
                <span
                  className="guard-shifts-page__avatar"
                  style={group.guardAvatarUrl ? undefined : getAvatarAccentStyle(group.guardId || group.guardName)}
                >
                  {group.guardAvatarUrl
                    ? <img src={group.guardAvatarUrl} alt={group.guardName} />
                    : initials(group.guardName)}
                </span>

                <div className="guard-shifts-page__guard-copy">
                  <h2>{group.guardName}</h2>
                  <p>{group.totalShifts} shifts on roster · {formatDuration(group.totalMinutes)}</p>
                </div>
              </div>

              <div className="guard-shifts-page__week-grid">
                {weekDates.map((dateKey) => {
                  const dayShiftsForGuard = group.shiftsByDate[dateKey] ?? [];
                  const isToday = dateKey === todayKey;
                  const isDraftDay = dayShiftsForGuard.some((shift) => !shift.is_published);

                  return (
                    <div
                      key={`${group.guardId}-${dateKey}`}
                      className={[
                        'guard-shifts-page__week-day',
                        isToday ? 'guard-shifts-page__week-day--today' : '',
                        isDraftDay ? 'guard-shifts-page__week-day--draft' : '',
                      ].filter(Boolean).join(' ')}
                    >
                      <div className="guard-shifts-page__week-day-head">
                        <div>
                          <div className="guard-shifts-page__week-day-name">{formatDayOfWeek(dateKey)}</div>
                          <div className="guard-shifts-page__week-day-number">{formatDayNumber(dateKey)}</div>
                        </div>
                        <span className="guard-shifts-page__week-day-count">
                          {dayShiftsForGuard.length}
                        </span>
                      </div>

                      {dayShiftsForGuard.length === 0 ? (
                        <div className="guard-shifts-page__week-empty">{t('Off roster')}</div>
                      ) : (
                        <div className="guard-shifts-page__week-shift-list">
                          {dayShiftsForGuard.map((shift) => {
                            const dutyCount = shift.segments.filter((segment) => segment.segment_type === 'duty').length;
                            const breakCount = shift.segments.length - dutyCount;
                            const warningCount = shift.rule_violations.length;

                            return (
                              <article
                                key={shift.id}
                                className={`guard-shifts-page__week-shift${shift.is_published ? '' : ' guard-shifts-page__week-shift--draft'}${warningCount > 0 ? ' guard-shifts-page__week-shift--warning' : ''}`}
                              >
                                <div className="guard-shifts-page__week-shift-time">
                                  {formatMinute(shift.start_minute)} - {formatMinute(shift.end_minute)}
                                </div>
                                <div className="guard-shifts-page__week-shift-title">{firstDutyLabel(shift)}</div>
                                <div className="guard-shifts-page__week-shift-meta">
                                  <span>{formatDuration(shift.end_minute - shift.start_minute)}</span>
                                  <span>{dutyCount} duties</span>
                                  <span>{breakCount} breaks</span>
                                </div>
                                <label className="guard-shifts-page__welfare-control guard-shifts-page__welfare-control--compact">
                                  <span>{t('Welfare')}</span>
                                  <select
                                    value={shift.welfare_check_mode}
                                    disabled={savingWelfareShiftId === shift.id}
                                    onChange={(event) => {
                                      void handleWelfareModeChange(
                                        shift.id,
                                        event.target.value as ShiftWelfareMode,
                                      );
                                    }}
                                  >
                                    <option value="inherit">{t('Site default')}</option>
                                    <option value="required">{t('Required')}</option>
                                    <option value="disabled">{t('Disabled')}</option>
                                  </select>
                                </label>
                                {warningCount > 0 && (
                                  <div className="guard-shifts-page__warning" title={shift.rule_violations.map((violation) => violation.message).join('\n')}>
                                    {warningCount} warning{warningCount === 1 ? '' : 's'}
                                  </div>
                                )}
                              </article>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          ))
        ) : (
          visibleGuardGroups.map((group) => (
            <section key={group.guardId} className="guard-shifts-page__guard-group">
              <div className="guard-shifts-page__guard-header">
                <span
                  className="guard-shifts-page__avatar"
                  style={group.guardAvatarUrl ? undefined : getAvatarAccentStyle(group.guardId || group.guardName)}
                >
                  {group.guardAvatarUrl
                    ? <img src={group.guardAvatarUrl} alt={group.guardName} />
                    : initials(group.guardName)}
                </span>

                <div className="guard-shifts-page__guard-copy">
                  <h2>{group.guardName}</h2>
                  <p>{group.shifts.length} shift{group.shifts.length === 1 ? '' : 's'} on roster · {formatDuration(group.totalMinutes)}</p>
                </div>
              </div>

              <div className="guard-shifts-page__shift-grid">
                {group.shifts.map((shift) => {
                  const dutyCount = shift.segments.filter((segment) => segment.segment_type === 'duty').length;
                  const breakCount = shift.segments.length - dutyCount;
                  const warningCount = shift.rule_violations.length;

                  return (
                    <article
                      key={shift.id}
                      className={`guard-shifts-page__shift-card${shift.is_published ? '' : ' guard-shifts-page__shift-card--draft'}${warningCount > 0 ? ' guard-shifts-page__shift-card--warning' : ''}`}
                    >
                      <div className="guard-shifts-page__shift-head">
                        <div>
                          <div className="guard-shifts-page__shift-time">
                            {formatMinute(shift.start_minute)} - {formatMinute(shift.end_minute)}
                          </div>
                          <div className="guard-shifts-page__shift-duration">{formatDuration(shift.end_minute - shift.start_minute)}</div>
                        </div>

                        <span className={`guard-shifts-page__badge ${shift.is_published ? 'guard-shifts-page__badge--published' : 'guard-shifts-page__badge--draft'}`}>
                          {shift.is_published ? 'Published' : 'Draft'}
                        </span>
                        {warningCount > 0 && (
                          <span className="guard-shifts-page__badge guard-shifts-page__badge--warning" title={shift.rule_violations.map((violation) => violation.message).join('\n')}>
                            {warningCount} warning{warningCount === 1 ? '' : 's'}
                          </span>
                        )}
                      </div>

                      <div className="guard-shifts-page__shift-meta">
                        <span>{dutyCount} duties</span>
                        <span>{breakCount} breaks</span>
                        <span>{shift.source_segment_count} merged segments</span>
                      </div>

                      <label className="guard-shifts-page__welfare-control">
                        <span>{t('Welfare checks')}</span>
                        <select
                          value={shift.welfare_check_mode}
                          disabled={savingWelfareShiftId === shift.id}
                          onChange={(event) => {
                            void handleWelfareModeChange(
                              shift.id,
                              event.target.value as ShiftWelfareMode,
                            );
                          }}
                        >
                          <option value="inherit">{t('Use site policy')}</option>
                          <option value="required">{t('Always require for this shift')}</option>
                          <option value="disabled">{t('Disable for this shift')}</option>
                        </select>
                      </label>

                      <ShiftSegmentFlow
                        shift={shift}
                        formatMinute={formatMinute}
                        formatDuration={formatDuration}
                      />
                    </article>
                  );
                })}
              </div>
            </section>
          ))
        )}
      </div>
    </div>
  );
}
